import { Box, CircularProgress, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";

import { AccountSwitcherOption } from "./AccountSwitcherOption";
import {
  type AgentUser,
  type AgentDetails,
  type WorkplaceDetails,
  type WorkplaceUser,
} from "./types";
import { type AccountSwitcherUser } from "./useAccountSwitcherHook";

interface AccountSwitcherOptionGroupProps {
  userDetails: AgentDetails | WorkplaceDetails;
  handleChange: (user: AccountSwitcherUser) => void;
}

export function AccountSwitcherOptionGroup(props: AccountSwitcherOptionGroupProps) {
  const { userDetails, handleChange } = props;
  const listCount = userDetails?.list.length || 0;

  if (!userDetails.loading && !listCount) {
    return (
      <Text italic variant="body2" color={(theme) => theme.palette.navbar?.itemContent} padding={2}>
        No Records
      </Text>
    );
  }

  const userList: AgentUser[] | WorkplaceUser[] | undefined = userDetails?.list;

  return (
    <Stack gap={2}>
      {userList.map((user: AgentUser | WorkplaceUser) => (
        <AccountSwitcherOption
          key={user.id}
          headline={user.userType === "AGENT" ? `${user.name} · ${user.email}` : user.name}
          subtitles={
            user.userType === "AGENT"
              ? [`${user.phone} · ${user.id}`]
              : [`${user.id} · ${user.pricingMsa}`]
          }
          stage={user.userType === "AGENT" ? user.stage : user.status}
          onClick={() => {
            handleChange({ user: { userId: user.userId, name: user.name }, type: user.userType });
          }}
        />
      ))}
      {userDetails.loading || userDetails.isFetchingNextPage ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
            gap: 1,
          }}
        >
          <CircularProgress
            size={16}
            sx={(theme) => ({ color: theme.palette.navbar?.itemContent })}
          />
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.navbar?.itemContent })}>
            Loading...
          </Text>
        </Box>
      ) : (
        userDetails.hasNextPage && (
          <Button
            fullWidth
            variant="secondary"
            onClick={async () => await userDetails.fetchNextPage()}
          >
            Load more results
          </Button>
        )
      )}
    </Stack>
  );
}
